// frontend/src/pages/reports/ReportDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Spin,
  message,
  Menu,
} from 'antd';
import {
  LineChartOutlined,
  CarOutlined,
  ClusterOutlined,
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import apiService from '../../services/api';

const { Title, Text } = Typography;

const ReportDashboard = () => {
  const [financialStats, setFinancialStats] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    netIncome: 0,
    incomeJobsCount: 0
  });
  const [incomeByChannel, setIncomeByChannel] = useState([]);
  const [receivedIncomeByChannel, setReceivedIncomeByChannel] = useState([]);
  const [expenseByCategory, setExpenseByCategory] = useState([]);
  const [paidExpenseByCategory, setPaidExpenseByCategory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardSummary = async () => {
      try {
        const res = await apiService.reports.getDashboardSummary();
        setFinancialStats(res.data.financialStats || {
          totalIncome: 0,
          totalExpenses: 0,
          netIncome: 0,
          incomeJobsCount: 0
        });
        setIncomeByChannel(Object.entries(res.data.incomeByChannel || {}).map(([name, value]) => ({ name, value })));
        setReceivedIncomeByChannel(Object.entries(res.data.receivedIncomeByChannel || {}).map(([name, value]) => ({ name, value })));
        setExpenseByCategory(Object.entries(res.data.expenseByCategory || {}).map(([name, value]) => ({ name, value })));
        setPaidExpenseByCategory(Object.entries(res.data.paidExpenseByCategory || {}).map(([name, value]) => ({ name, value })));
      } catch (err) {
        message.error('Failed to fetch dashboard summary');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardSummary();
  }, []);

  // Helper function to round to 2 decimal places
  const roundToTwoDecimals = (value) => {
    return Number(Number(value || 0).toFixed(2));
  };

  // Combine income data to show paid and unpaid portions
  const incomeByChannelWithPaidUnpaid = incomeByChannel.map(channel => {
    const paidChannel = receivedIncomeByChannel.find(c => c.name === channel.name);
    const paid = roundToTwoDecimals(paidChannel ? paidChannel.value : 0);
    const total = roundToTwoDecimals(channel.value);
    const unpaid = Math.max(0, roundToTwoDecimals(total - paid));
    return {
      name: channel.name,
      paid: paid,
      unpaid: unpaid
    };
  });

  // Custom tooltip formatter to show 2 decimal places
  const formatTooltipValue = (value) => {
    return `£${roundToTwoDecimals(value).toFixed(2)}`;
  };

  // Custom axis formatter to show 2 decimal places
  const formatAxisValue = (value) => {
    return roundToTwoDecimals(value).toFixed(2);
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Reports Dashboard</Title>
        <Text type="secondary">View and analyze business performance metrics</Text>
      </div>

      <Menu
        mode="horizontal"
        selectedKeys={['dashboard']}
        style={{ marginBottom: '24px' }}
      >
        <Menu.Item key="dashboard" icon={<LineChartOutlined />}>
          <Link to="/reports">Dashboard</Link>
        </Menu.Item>
        <Menu.Item key="financial" icon={<span style={{ fontSize: '16px' }}>£</span>}>
          <Link to="/reports/financial">Financial</Link>
        </Menu.Item>
        <Menu.Item key="van" icon={<CarOutlined />}>
          <Link to="/reports/van">Vans</Link>
        </Menu.Item>
        <Menu.Item key="resources" icon={<ClusterOutlined />}>
          <Link to="/reports/resources">Resource Utilization</Link>
        </Menu.Item>
      </Menu>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Total Income"
              value={financialStats.totalIncome}
              precision={2}
              valueStyle={{ color: '#3f8600' }}
              prefix="£"
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              VAT: £{(financialStats.totalIncomeVAT || 0).toFixed(2)}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Total Expenses"
              value={financialStats.totalExpenses}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
              prefix="£"
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              VAT: £{(financialStats.totalExpensesVAT || 0).toFixed(2)}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Net Income"
              value={financialStats.netIncome}
              precision={2}
              valueStyle={{ color: financialStats.netIncome >= 0 ? '#3f8600' : '#cf1322' }}
              prefix="£"
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              VAT: £{((financialStats.totalIncomeVAT || 0) - (financialStats.totalExpensesVAT || 0)).toFixed(2)}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Income Jobs"
              value={financialStats.incomeJobsCount}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              &nbsp;
            </div>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Received Income"
              value={financialStats.paidIncome}
              precision={2}
              valueStyle={{ color: '#3f8600' }}
              prefix="£"
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              VAT: £{(financialStats.paidIncomeVAT || 0).toFixed(2)}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Paid Expenses"
              value={financialStats.paidExpenses}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
              prefix="£"
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              VAT: £{(financialStats.paidExpensesVAT || 0).toFixed(2)}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Replaced Charts */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Income Distribution by Job Channel">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={incomeByChannelWithPaidUnpaid}
                layout="vertical"
                margin={{ left: 40, right: 20, top: 20, bottom: 20 }}
              >
                <XAxis 
                  type="number" 
                  allowDecimals={true} 
                  tickFormatter={formatAxisValue}
                />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip formatter={formatTooltipValue} />
                <Legend />
                <Bar dataKey="paid" stackId="income" fill="#3f8600" name="Paid" />
                <Bar dataKey="unpaid" stackId="income" fill="#ff4d4f" name="Unpaid" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Expense Distribution by Category">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={expenseByCategory.map(item => ({ ...item, value: roundToTwoDecimals(item.value) }))}
                layout="vertical"
                margin={{ left: 40, right: 20, top: 20, bottom: 20 }}
              >
                <XAxis 
                  type="number" 
                  allowDecimals={true} 
                  tickFormatter={formatAxisValue}
                />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip formatter={formatTooltipValue} />
                <Legend />
                <Bar dataKey="value" fill="#FF8042" name="Expense" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Received Income Distribution by Channel">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={receivedIncomeByChannel.map(item => ({ ...item, value: roundToTwoDecimals(item.value) }))}
                layout="vertical"
                margin={{ left: 40, right: 20, top: 20, bottom: 20 }}
              >
                <XAxis 
                  type="number" 
                  allowDecimals={true} 
                  tickFormatter={formatAxisValue}
                />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip formatter={formatTooltipValue} />
                <Legend />
                <Bar dataKey="value" fill="#3f8600" name="Received Income" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Paid Expense Distribution by Category">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={paidExpenseByCategory.map(item => ({ ...item, value: roundToTwoDecimals(item.value) }))}
                layout="vertical"
                margin={{ left: 40, right: 20, top: 20, bottom: 20 }}
              >
                <XAxis 
                  type="number" 
                  allowDecimals={true} 
                  tickFormatter={formatAxisValue}
                />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip formatter={formatTooltipValue} />
                <Legend />
                <Bar dataKey="value" fill="#cf1322" name="Paid Expense" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ReportDashboard;